'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Settings, Clock, DollarSign, ToggleLeft, ToggleRight } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { workstations } from '../data/mock-data';
import type { Workstation } from '../types';

// 작업장 등록 모달
function WorkstationAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="작업장 등록"
      description="새로운 제조 작업장을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="작업장 코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="작업장명" required>
            <FormInput placeholder="작업장명을 입력하세요" />
          </FormField>
          <FormField label="작업장 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="골판지 제조">골판지 제조</option>
              <option value="인쇄">인쇄</option>
              <option value="톰슨가공">톰슨가공</option>
              <option value="접착/조립">접착/조립</option>
              <option value="검품/포장">검품/포장</option>
            </FormSelect>
          </FormField>
          <FormField label="시간당 단가" required>
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="일일 생산능력" required>
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="상태">
            <FormSelect>
              <option value="active">활성</option>
              <option value="inactive">비활성</option>
            </FormSelect>
          </FormField>
        </div>
        <FormField label="설명">
          <FormTextarea rows={3} placeholder="작업장에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function WorkstationListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return workstations.filter((ws) => {
      const matchesSearch =
        ws.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ws.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === 'all' || ws.workstationType === selectedType;
      const matchesActive = showInactive || ws.isActive;

      return matchesSearch && matchesType && matchesActive;
    });
  }, [searchQuery, selectedType, showInactive]);

  // 작업장 유형 목록
  const workstationTypes = useMemo(() => {
    const types = new Set(workstations.map((ws) => ws.workstationType));
    return Array.from(types);
  }, []);

  // 통계
  const stats = useMemo(() => {
    const active = workstations.filter((ws) => ws.isActive).length;
    const totalCapacity = workstations
      .filter((ws) => ws.isActive)
      .reduce((sum, ws) => sum + ws.capacity, 0);
    const avgHourRate =
      workstations.reduce((sum, ws) => sum + ws.hourRate, 0) / workstations.length;

    return { active, totalCapacity, avgHourRate };
  }, []);

  const columns = [
    {
      key: 'code',
      label: '작업장 코드',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'name',
      label: '작업장명',
      width: '22%',
      sortable: true,
      render: (value: unknown, row: Workstation) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <Settings className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            <div className="text-xs text-muted-foreground truncate">{row.workstationType}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'hourRate',
      label: '시간당 단가',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">₩{(value as number).toLocaleString()}</span>
      ),
    },
    {
      key: 'capacity',
      label: '생산능력',
      width: '10%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{String(value)}/일</span>
      ),
    },
    {
      key: 'isActive',
      label: '상태',
      width: '8%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'description',
      label: '설명',
      width: '24%',
      render: (value: unknown) => (
        <span className="text-sm text-muted-foreground truncate block">{String(value || '-')}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="작업장 관리"
        description="제조 작업장을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            작업장 등록
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <Settings className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">활성 작업장</div>
              <div className="text-2xl font-bold">{stats.active}개</div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">총 생산능력</div>
              <div className="text-2xl font-bold">{stats.totalCapacity}/일</div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">평균 시간단가</div>
              <div className="text-2xl font-bold">₩{stats.avgHourRate.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="작업장 코드, 이름 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 유형 필터 */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          {workstationTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {/* 비활성 표시 토글 */}
        <button
          onClick={() => setShowInactive(!showInactive)}
          className={`h-10 px-4 rounded-lg border text-sm flex items-center gap-2 transition-colors ${
            showInactive
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-background text-muted-foreground'
          }`}
        >
          {showInactive ? (
            <ToggleRight className="w-4 h-4" />
          ) : (
            <ToggleLeft className="w-4 h-4" />
          )}
          비활성 포함
        </button>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 작업장 등록 모달 */}
      <WorkstationAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
